import { useMemo, useState } from "react"

import {
  PortfolioPageObj,
  PortfolioPageObjClass
} from "scrivito/Objs/PortfolioPage/PortfolioPageObjClass"
import { ProductCategoryObj } from "scrivito/Objs/ProductCategory/ProductCategoryObjClass"
import { getCurrentSiteID } from "shared/lib/getCurrentSiteID"
import { useDebounce } from "shared/lib/performance/useDebounce"
import { useEvent } from "shared/lib/useEvent"

import { useProductGroupOptions } from "entities/product-groups/model/useProductGroupOptions"
import { useProductFilter } from "entities/products/model/useProductFilter"
import { ProductFilterFormData } from "entities/products/types/ProductFilter"

import { PortfolioProductSelectModel } from "../PortfolioProductSelect"

export interface UsePortfolioProductSelectModelArgs {
  onProductSelect: (productType: string) => void
}

export function usePortfolioProductSelectModel({
  onProductSelect
}: UsePortfolioProductSelectModelArgs): PortfolioProductSelectModel {
  const portfolio: PortfolioPageObj | undefined =
    PortfolioPageObjClass.onSite(getCurrentSiteID()).all().first() || undefined

  const [selectedProductCategory, setSelectedProductCategory] =
    useState<ProductCategoryObj | null>(null)

  const [productFilterFormData, setProductFilterFormData] =
    useState<ProductFilterFormData>({})

  const debouncedSearchTerm = useDebounce(
    productFilterFormData.searchTerm ?? ""
  )
  const debouncedProductFilterFormData = useMemo(
    () => ({
      ...productFilterFormData,
      searchTerm: debouncedSearchTerm
    }),
    [productFilterFormData, debouncedSearchTerm]
  )

  const products = useProductFilter(debouncedProductFilterFormData, {
    category: selectedProductCategory ?? undefined
  })

  const handleProductCategorySelect = useEvent(
    (productCategoryObj: ProductCategoryObj | null) => {
      setSelectedProductCategory(productCategoryObj)
      setProductFilterFormData({})
    }
  )

  const productGroupOptions = useProductGroupOptions({
    productCategoryObj: selectedProductCategory || undefined
  })

  const productCategories: ProductCategoryObj[] | undefined = portfolio
    ?.orderedChildren()
    .filter(
      productCategory =>
        !(productCategory as ProductCategoryObj).get("isHiddenInPortfolio")
    ) as ProductCategoryObj[] | undefined

  return {
    portfolio,
    products,
    productGroupOptions,
    productCategories,
    filter: debouncedProductFilterFormData,
    onProductSelect,
    onProductFilterChange: setProductFilterFormData,
    onProductCategorySelect: handleProductCategorySelect
  }
}
